import includes from 'ramda/es/includes'
import { analyzeLocalStorage } from '../LocalStorageAnalyzer.js'


export class AbstractModel {
    constructor(source) {
        this.update(source)
    }

    update(source) {
        if (!source) source = {}

        Object.defineProperty(this, 'source', {
            configurable: true, // Allows deletion of the 'source' property and modification of its descriptors.
            writable: false, // Prevents direct modification of the 'source' property after initialization with '='
            value: source.getSource ? source.getSource() : source,
        })
    }

    isEmpty() {
        return includes(this.source, [undefined, null, {}])
    }
    getSource() {
        return this.source
    }

    getByAliases(aliases, defaultsTo) {
        let result = defaultsTo
        aliases.forEach((a) => {
            if (!includes(this.source[a], [undefined, null, '']))
                result = this.source[a]
        })
        return result
    }

    static log(x) {
        console.log("AbstractModel:", x)
        return x
    }

    static getters() {
        return this.store().getters
    }
    static dispatch(name, payload) {
        try {
            this.store().dispatch(name, payload);
        } catch (e) {
            if (e.name === 'QuotaExceededError' || e.message.includes('quota')) {
                console.warn('🛒 QuotaExceededError detected, attempting to free space...');
                
                // Log localStorage sizes
                this.logLocalStorageSizes();
                throw e; // Re-throw after logging sizes        
            } else {
                throw e; // Re-throw if not quota error
            }
        }
    }

    static logLocalStorageSizes() {
        let totalSize = 0;
        console.log('🛒 localStorage sizes (>5KB only, sorted by size, excluding dummy keys):');
        console.log(`  (Running localStorage.length = ${localStorage.length})`);
        
        // Collect size data
        let sizeData = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            const value = localStorage.getItem(key);
            const sizeBytes = new Blob([value]).size;
            totalSize += sizeBytes;
            
            // Skip dummy keys for logging
            if (key.startsWith('dummy')) continue;
            
            // If it's the Vuex store key, break it down
            if (key.includes('@')) {  // Assuming Vuex key format
                try {
                    const storeData = JSON.parse(value);
                    sizeData.push({ name: key, size: sizeBytes, type: 'store' });
                    
                    // Break down modules
                    for (const module in storeData) {
                        if (storeData.hasOwnProperty(module)) {
                            const moduleSize = new Blob([JSON.stringify(storeData[module])]).size;
                            sizeData.push({ name: `${key}.${module}`, size: moduleSize, type: 'module' });
                            
                            // Break down sub-properties
                            if (storeData[module] && typeof storeData[module] === 'object') {
                                for (const subKey in storeData[module]) {
                                    if (storeData[module].hasOwnProperty(subKey)) {
                                        const subSize = new Blob([JSON.stringify(storeData[module][subKey])]).size;
                                        sizeData.push({ name: `${key}.${module}.${subKey}`, size: subSize, type: 'sub' });
                                    }
                                }
                            }
                        }
                    }
                } catch (e) {
                    // Not JSON or parse error, treat as regular key
                    sizeData.push({ name: key, size: sizeBytes, type: 'key' });
                }
            } else {
                sizeData.push({ name: key, size: sizeBytes, type: 'key' });
            }
        }
        
        // Sort by size descending
        sizeData.sort((a, b) => b.size - a.size);
        
        // Log only >5KB
        sizeData.forEach(item => {
            if (item.size > 5120) {  // 5KB
                const kb = (item.size / 1024).toFixed(2);
                const indent = item.type === 'sub' ? '    ' : item.type === 'module' ? '  ' : '';
                console.log(`${indent}${item.name}: ${item.size} bytes (${kb} KB)`);
            }
        });
        
        const totalKB = (totalSize / 1024).toFixed(2);
        console.log(`  Total: ${totalSize} bytes (${totalKB} KB)`);
    }

    static store() {
        return window.$_store
    }
    static ebus() {
        return window.$_ebus
    }
    static http() {
        return window.$_http
    }
    static router() {
        return window.$_router
    }
}

export default AbstractModel
